import { useState } from "react";
import { Plus, Eye, EyeOff } from "lucide-react";
import { registerTenantClient } from "../lib/sailpoint";
import { Field, Input, PrimaryButton } from "./ui";
import { normalizeTenantInput, isFullHostTenant, tenantApiHost } from "../lib/tenantHost";

/**
 * Register (or re-register) a tenant's sign-in OAuth client — the tenant
 * admin's PAT is used once server-side to create the client and never
 * stored. Shared by the Local Admin registry screen (full replace rights)
 * and the native app's login screen (first-time setup of a NEW tenant only;
 * the server refuses anonymous re-registration of an existing one).
 */
export function RegisterTenantForm({ onDone }) {
  const [tenant, setTenant] = useState("");
  const [siteName, setSiteName] = useState("");
  const [adminClientId, setAdminClientId] = useState("");
  const [adminClientSecret, setAdminClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    // A value with a "." is a full host and is registered exactly as typed
    // (only https:// is added); a plain name goes on the default domain.
    const t = normalizeTenantInput(tenant);
    if (!t || !adminClientId.trim() || !adminClientSecret) {
      setError("Tenant, client ID, and client secret are required.");
      return;
    }
    setBusy(true);
    try {
      await registerTenantClient({
        tenant: t,
        adminClientId: adminClientId.trim(),
        adminClientSecret,
        siteName: siteName.trim() || t,
      });
      setTenant(""); setSiteName(""); setAdminClientId(""); setAdminClientSecret("");
      onDone(t);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Registration failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="px-5 py-4">
      <Field label="Tenant name">
        <Input value={tenant} onChange={(e) => setTenant(e.target.value)} placeholder="your-org  or  your-org.api.identitynow.com" autoCapitalize="none" autoCorrect="off" />
        <p className="text-xs text-gray-500 mt-1.5">
          {isFullHostTenant(normalizeTenantInput(tenant))
            ? <>Full host — used exactly as entered: <span className="font-mono text-gray-700">https://{tenantApiHost(normalizeTenantInput(tenant))}</span>. Enter the tenant's <span className="font-medium">API</span> host (it has an “.api” part), not its web address.</>
            : normalizeTenantInput(tenant)
            ? <>Will connect to <span className="font-mono text-gray-700">https://{tenantApiHost(normalizeTenantInput(tenant))}</span>. For a tenant on another domain, enter its full API host instead, e.g. your-org.api.identitynow.com.</>
            : <>A plain name connects to <span className="font-mono">name.api.identitynow-demo.com</span>. A name containing a “.” is used as the complete host, exactly as entered.</>}
        </p>
      </Field>
      <Field label="Site name">
        <Input value={siteName} onChange={(e) => setSiteName(e.target.value)} placeholder="Shown in the login dropdown" autoCapitalize="none" autoCorrect="off" />
      </Field>
      <Field label="Admin PAT Client ID">
        <Input value={adminClientId} onChange={(e) => setAdminClientId(e.target.value)} placeholder="PAT Client ID" autoCapitalize="none" autoCorrect="off" />
      </Field>
      <Field label="Admin PAT Client secret">
        <div className="relative">
          <Input
            type={showSecret ? "text" : "password"}
            value={adminClientSecret}
            onChange={(e) => setAdminClientSecret(e.target.value)}
            placeholder="PAT Client secret"
            autoCapitalize="none"
            autoCorrect="off"
          />
          <button type="button" onClick={() => setShowSecret((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" tabIndex={-1}>
            {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </Field>
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-sm text-red-700">{error}</div>
      )}
      <PrimaryButton type="submit" loading={busy}>
        <Plus size={16} />
        {busy ? "Registering…" : "Register tenant"}
      </PrimaryButton>
    </form>
  );
}
